import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from 'components/formComponents/Icons/WarningIcon';
import styles from './ReportDismissedSection.module.scss';
import { CYPRESS_DATA } from './constants';
import EditableCell from './EditableCell';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  EmployeeRow,
  EmployeeRowStatus,
} from './types';
import { RegisterDismissedUiTexts, replaceTemplateTokens } from './texts';

type Props = {
  rows: EmployeeRow[];
  texts: RegisterDismissedUiTexts;
  emptyInfoText?: string | null;
  onRemove: (fieldId: string) => void;
  onEditingChange: (
    fieldId: string,
    fieldName: string,
    isEditing: boolean
  ) => void;
};

const StatusIcon = ({ status }: { status: EmployeeRowStatus }) => {
  if (status === 'error') {
    return <ErrorOutlineIcon className={styles.statusError} fontSize="small" />;
  }
  if (status === 'warning') {
    return <WarningIcon className={styles.statusWarning} fontSize="small" />;
  }
  return <CheckIcon className={styles.statusValid} fontSize="small" />;
};

const employmentTypeLabel = (value: string): string =>
  EMPLOYMENT_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? '';

const TerminatedEmployeesTableBasic = ({
  rows,
  texts,
  emptyInfoText,
  onRemove,
  onEditingChange,
}: Props) => {
  const removeRowActionLabel = texts.removeRowLabel.includes('{name}')
    ? texts.removeRowLabel.replace('{name}', '').trim()
    : texts.removeRowLabel;

  const statusLabels = {
    error: 'Behöver korrigeras',
    warning: 'Kontrollera uppgifterna',
    valid: 'Klar',
  };

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table} data-cy={CYPRESS_DATA.table}>
        <thead>
          <tr>
            <th className={styles.statusCell} scope="col">
              <span className={styles.visuallyHidden}>Status</span>
            </th>
            <th scope="col">{texts.columnSsn}</th>
            <th scope="col">{texts.columnFirstName}</th>
            <th scope="col">{texts.columnLastName}</th>
            <th scope="col">{texts.columnEmploymentType}</th>
            <th scope="col">{texts.columnStartDate}</th>
            <th scope="col">{texts.columnTerminationDate}</th>
            <th scope="col">{texts.columnEndDate}</th>
            <th scope="col">{texts.columnEmail}</th>
            <th scope="col">{texts.columnPhone}</th>
            <th className={styles.actionCell} scope="col">
              <span className={styles.visuallyHidden}>
                {removeRowActionLabel}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && emptyInfoText && (
            <tr data-cy={CYPRESS_DATA.allCorrected}>
              <td className={styles.emptyInfoRow} colSpan={11}>
                <span dangerouslySetInnerHTML={{ __html: emptyInfoText }} />
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const prefix = `terminatedEmployees.${row.formIndex}`;
            const rowWarning = row.warnings.find((warning) => !warning.field);
            const noNoticePeriodWarning = row.warnings.find(
              (warning) => warning.field === 'terminationDate'
            );
            const startDateWarning = row.warnings.find(
              (warning) => warning.field === 'startDate'
            );
            const endDateWarning = row.warnings.find(
              (warning) => warning.field === 'endDate'
            );
            return (
              <tr
                key={row.fieldId}
                data-cy={CYPRESS_DATA.row}
                data-status={row.status}
              >
                <td
                  className={styles.statusCell}
                  data-cy={CYPRESS_DATA.rowStatus}
                >
                  <StatusIcon status={row.status} />
                  <span className={styles.visuallyHidden}>
                    {statusLabels[row.status]}
                  </span>
                </td>
                <td className={styles.cell}>
                  <EditableCell
                    name={`${prefix}.socialSecurityNumber`}
                    label={texts.columnSsn}
                    value={row.employee.socialSecurityNumber}
                    error={row.errors.socialSecurityNumber}
                    onEditingChange={(fieldName, editing) =>
                      onEditingChange(row.fieldId, fieldName, editing)
                    }
                  />
                </td>
                <td className={styles.cell}>
                  <EditableCell
                    name={`${prefix}.firstName`}
                    label={texts.columnFirstName}
                    value={row.employee.firstName}
                    error={row.errors.firstName}
                    onEditingChange={(fieldName, editing) =>
                      onEditingChange(row.fieldId, fieldName, editing)
                    }
                  />
                </td>
                <td className={styles.cell}>
                  <EditableCell
                    name={`${prefix}.lastName`}
                    label={texts.columnLastName}
                    value={row.employee.lastName}
                    error={row.errors.lastName}
                    onEditingChange={(fieldName, editing) =>
                      onEditingChange(row.fieldId, fieldName, editing)
                    }
                  />
                </td>
                <td className={styles.cell}>
                  <EditableCell
                    name={`${prefix}.employmentType`}
                    label={texts.columnEmploymentType}
                    value={row.employee.employmentType}
                    displayValue={employmentTypeLabel(
                      row.employee.employmentType
                    )}
                    options={EMPLOYMENT_TYPE_OPTIONS}
                    error={row.errors.employmentType}
                    onEditingChange={(fieldName, editing) =>
                      onEditingChange(row.fieldId, fieldName, editing)
                    }
                  />
                </td>
                <td className={styles.cell}>
                  <EditableCell
                    name={`${prefix}.startDate`}
                    label={texts.columnStartDate}
                    type="date"
                    value={row.employee.startDate}
                    error={row.errors.startDate}
                    warning={startDateWarning?.message}
                    onEditingChange={(fieldName, editing) =>
                      onEditingChange(row.fieldId, fieldName, editing)
                    }
                  />
                </td>
                <td className={styles.cell} colSpan={2}>
                  <div className={styles.dateCells}>
                    <div className={styles.contactCellItem}>
                      <EditableCell
                        name={`${prefix}.terminationDate`}
                        label={texts.columnTerminationDate}
                        type="date"
                        value={row.employee.terminationDate}
                        error={row.errors.terminationDate}
                        onEditingChange={(fieldName, editing) =>
                          onEditingChange(row.fieldId, fieldName, editing)
                        }
                      />
                    </div>
                    <div className={styles.contactCellItem}>
                      <EditableCell
                        name={`${prefix}.endDate`}
                        label={texts.columnEndDate}
                        type="date"
                        value={row.employee.endDate}
                        error={row.errors.endDate}
                        warning={endDateWarning?.message}
                        onEditingChange={(fieldName, editing) =>
                          onEditingChange(row.fieldId, fieldName, editing)
                        }
                      />
                    </div>
                    {noNoticePeriodWarning && (
                      <span
                        className={styles.rowWarningText}
                        title={noNoticePeriodWarning.message}
                      >
                        {noNoticePeriodWarning.message}
                      </span>
                    )}
                  </div>
                </td>
                <td className={styles.cell} colSpan={2}>
                  <div className={styles.contactCells}>
                    <div className={styles.contactCellItem}>
                      <EditableCell
                        name={`${prefix}.email`}
                        label={texts.columnEmail}
                        type="email"
                        value={row.employee.email}
                        error={row.errors.email}
                        onEditingChange={(fieldName, editing) =>
                          onEditingChange(row.fieldId, fieldName, editing)
                        }
                      />
                    </div>
                    <div className={styles.contactCellItem}>
                      <EditableCell
                        name={`${prefix}.phone`}
                        label={texts.columnPhone}
                        type="tel"
                        value={row.employee.phone}
                        error={row.errors.phone}
                        onEditingChange={(fieldName, editing) =>
                          onEditingChange(row.fieldId, fieldName, editing)
                        }
                      />
                    </div>
                    {rowWarning && (
                      <span
                        className={styles.rowWarningText}
                        title={rowWarning.message}
                      >
                        {rowWarning.message}
                      </span>
                    )}
                  </div>
                </td>
                <td className={styles.actionCell}>
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => onRemove(row.fieldId)}
                    aria-label={replaceTemplateTokens(texts.removeRowLabel, {
                      name: `${row.employee.firstName} ${row.employee.lastName}`.trim(),
                    })}
                    data-cy={CYPRESS_DATA.removeRow}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TerminatedEmployeesTableBasic;
